import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, Scale, Shield, AlertTriangle, FileText, Mail, CreditCard } from 'lucide-react';
import SiteFooter from '@/components/SiteFooter';

const TermsOfService = () => {
  const navigate = useNavigate();
  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-slate-400 hover:text-white"
            data-testid="back-to-app-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to App
          </Button>
          <div className="flex-1" />
          <FileText className="h-5 w-5 text-purple-400" />
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">Terms of Service</h1>
          <p className="text-slate-400">Last Updated: {currentDate}</p>
        </div>

        <div className="space-y-8 text-slate-300">
          {/* Introduction */}
          <section className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <p className="leading-relaxed">
              Welcome to Option Wizard (&ldquo;the Platform&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;). 
              By accessing or using our platform, you agree to be bound by these Terms of Service (&ldquo;Terms&rdquo;). 
              If you do not agree to these Terms, please do not use the Platform.
            </p>
          </section>

          {/* Section 1: Acceptance of Terms */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <FileText className="h-5 w-5 text-purple-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white">1. Acceptance of Terms</h2>
            </div>
            <div className="pl-12 space-y-3">
              <p>By using Option Wizard, you acknowledge that you have read, understood, and agree to be bound by these Terms. You also agree to comply with all applicable laws and regulations.</p>
              <p>We reserve the right to modify these Terms at any time. Continued use of the Platform after any modifications constitutes your acceptance of the revised Terms.</p>
            </div>
          </section>

          {/* Section 2: Educational Purpose */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Scale className="h-5 w-5 text-blue-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white">2. Educational Purpose & Disclaimer</h2>
            </div>
            <div className="pl-12 space-y-3">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <p className="font-semibold text-amber-400 mb-2">IMPORTANT NOTICE</p>
                <p>This Platform is designed solely for <strong className="text-white">educational and informational purposes</strong>. Nothing on this Platform constitutes financial advice, investment advice, trading advice, or any other form of professional advice.</p>
              </div>
              <p>All content, including but not limited to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>AI-generated analysis and recommendations</li>
                <li>Option strategy suggestions</li>
                <li>Market data and visualizations</li>
                <li>Portfolio analysis tools</li>
                <li>Price predictions and forecasts</li>
              </ul>
              <p>...is provided for educational purposes only and should not be construed as a recommendation to buy, sell, or hold any securities.</p>
            </div>
          </section>

          {/* Section 3: Risk Acknowledgment */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white">3. Risk Acknowledgment</h2>
            </div>
            <div className="pl-12 space-y-3">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="font-semibold text-red-400">OPTIONS TRADING INVOLVES SUBSTANTIAL RISK</p>
                <p className="mt-2">Options trading is not suitable for all investors. You can lose more than your initial investment. Past performance is not indicative of future results.</p>
              </div>
              <p>By using this Platform, you acknowledge and agree that:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You understand the risks involved in options trading</li>
                <li>You are solely responsible for your own investment decisions</li>
                <li>You will not hold the Platform liable for any trading losses</li>
                <li>You should consult with a qualified financial advisor before making any investment decisions</li>
                <li>You are trading with money you can afford to lose</li>
              </ul>
            </div>
          </section>

          {/* Section 4: No Warranties */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-500/20 rounded-lg">
                <Shield className="h-5 w-5 text-slate-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white">4. No Warranties</h2>
            </div>
            <div className="pl-12 space-y-3">
              <p>The Platform is provided &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE&rdquo; without any warranties of any kind, either express or implied, including but not limited to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Implied warranties of merchantability</li>
                <li>Fitness for a particular purpose</li>
                <li>Non-infringement</li>
                <li>Accuracy, reliability, or completeness of any information</li>
                <li>Uninterrupted or error-free service</li>
              </ul>
              <p>We do not warrant that the Platform will meet your requirements or that any errors will be corrected.</p>
            </div>
          </section>

          {/* Section 5: Data Accuracy */}
          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">5. Data Accuracy Disclaimer</h2>
            <div className="space-y-3">
              <p>Market data, options pricing, stock quotes, and other financial information displayed on this Platform:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>May be delayed, inaccurate, or incomplete</li>
                <li>Is sourced from third-party providers</li>
                <li>Should be verified with your broker before making any decisions</li>
                <li>Is not guaranteed for accuracy or timeliness</li>
              </ul>
              <p>We are not responsible for any errors or omissions in the data provided.</p>
            </div>
          </section>

          {/* Section 6: AI Limitations */}
          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">6. Artificial Intelligence Limitations</h2>
            <div className="space-y-3">
              <p>This Platform utilizes artificial intelligence (AI) to generate analysis, suggestions, and content. You acknowledge that:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>AI-generated content may contain errors, inaccuracies, or inappropriate suggestions</li>
                <li>AI models have limitations and may not account for all market conditions</li>
                <li>AI outputs should be considered as one of many inputs in your decision-making process</li>
                <li>AI recommendations should never be the sole basis for any financial decision</li>
                <li>We do not guarantee the accuracy or reliability of AI-generated content</li>
              </ul>
            </div>
          </section>

          {/* Section 7: Limitation of Liability */}
          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">7. Limitation of Liability</h2>
            <div className="space-y-3">
              <p>To the fullest extent permitted by applicable law, Option Wizard, its owners, operators, developers, affiliates, partners, and employees shall not be liable for any:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Direct, indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, revenue, data, or goodwill</li>
                <li>Trading losses or investment losses</li>
                <li>Damages arising from your use or inability to use the Platform</li>
                <li>Damages resulting from unauthorized access to your account</li>
                <li>Any other damages, however caused</li>
              </ul>
              <p>This limitation applies regardless of whether the damages are based on warranty, contract, tort, or any other legal theory.</p>
            </div>
          </section>

          {/* Section 8: User Responsibilities */}
          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">8. User Responsibilities</h2>
            <div className="space-y-3">
              <p>As a user of this Platform, you agree to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Provide accurate information when creating an account</li>
                <li>Keep your account credentials confidential</li>
                <li>Not use the Platform for any illegal or unauthorized purpose</li>
                <li>Not attempt to interfere with the proper functioning of the Platform</li>
                <li>Not reverse engineer, decompile, or disassemble any part of the Platform</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
            </div>
          </section>

          {/* Section 9: Intellectual Property */}
          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">9. Intellectual Property</h2>
            <div className="space-y-3">
              <p>All content on this Platform, including but not limited to text, graphics, logos, icons, images, audio clips, software, and code, is the property of Option Wizard or its content suppliers and is protected by intellectual property laws.</p>
              <p>You may not reproduce, distribute, modify, create derivative works of, publicly display, or exploit any content without our prior written consent.</p>
            </div>
          </section>

          {/* Section 10: Third-Party Services */}
          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">10. Third-Party Services & Brokerage Integration</h2>
            <div className="space-y-3">
              <p>The Platform may integrate with third-party services, including brokerage accounts (e.g., Tastytrade). When using these integrations:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>You are subject to the terms and conditions of those third-party services</li>
                <li>We are not responsible for the actions or policies of third-party services</li>
                <li>We do not guarantee the availability or accuracy of third-party data</li>
                <li>You authorize us to access your brokerage data as permitted by your authorization</li>
              </ul>
            </div>
          </section>

          {/* Section 11: Future Paid Services */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CreditCard className="h-5 w-5 text-green-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white">11. Subscriptions & Billing</h2>
            </div>
            <div className="pl-12 space-y-3">
              <p>Option Wizard offers a free Daily Pick feature; advanced tools require a paid Premium subscription. Current plans:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong className="text-white">Monthly:</strong> $19.99 per month (USD)</li>
                <li><strong className="text-white">Annual:</strong> $99.99 per year (USD)</li>
                <li><strong className="text-white">Free trial:</strong> a 1-day free trial is offered to eligible new subscribers (one trial per account)</li>
              </ul>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Subscriptions renew automatically at the end of each billing period unless cancelled beforehand.</li>
                <li>You can cancel at any time from the Stripe billing portal; access continues until the end of the current paid period.</li>
                <li>Payments are processed by our payment processor, Stripe; we do not store your card details.</li>
                <li>We may change pricing with notice; changes do not affect the period you have already paid for.</li>
                <li>Access to premium features may be suspended for failed or non-payment.</li>
                <li>Refunds are addressed in our <a href="/refund-policy" className="text-amber-300 underline underline-offset-4">Refund Policy</a>.</li>
              </ul>
            </div>
          </section>

          {/* Section 12: Termination */}
          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">12. Termination</h2>
            <div className="space-y-3">
              <p>We reserve the right to terminate or suspend your access to the Platform at any time, with or without cause, with or without notice. Upon termination:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Your right to use the Platform will immediately cease</li>
                <li>We may delete your account and associated data</li>
                <li>Provisions that by their nature should survive termination shall survive</li>
              </ul>
            </div>
          </section>

          {/* Section 13: Governing Law */}
          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">13. Governing Law & Dispute Resolution</h2>
            <div className="space-y-3">
              <p>
                These Terms are governed by and construed in accordance with the laws of the
                <strong> State of Hawaii, United States</strong>, without regard to its conflict-of-law principles. The
                parties will first attempt to resolve any dispute informally by contacting us. No binding-arbitration
                requirement is imposed by these Terms.
              </p>
              <p className="text-sm text-slate-400">
                Nothing in this section limits any non-waivable rights you may have under the mandatory laws that apply to you.
              </p>
            </div>
          </section>

          {/* Section 14: Indemnification */}
          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">14. Indemnification</h2>
            <div className="space-y-3">
              <p>You agree to indemnify, defend, and hold harmless Option Wizard and its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys&apos; fees) arising out of or related to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Your use of the Platform</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of another party</li>
                <li>Your trading activities</li>
              </ul>
            </div>
          </section>

          {/* Section 15: Contact */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <Mail className="h-5 w-5 text-indigo-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white">15. Contact Information</h2>
            </div>
            <div className="pl-12 space-y-3">
              <p>If you have any questions about these Terms of Service, please reach us through our <a href="/contact" className="text-amber-300 underline underline-offset-4">contact form</a>. Our mailing address is:</p>
              <address className="not-italic rounded-lg border border-slate-700 bg-slate-800/50 p-4 text-slate-200 leading-relaxed">
                Timothy Woods<br />Option Wizard<br />200 N. Vineyard Blvd<br />Ste A325-186<br />Honolulu, Hawaii 96817
              </address>
            </div>
          </section>

          {/* Final Agreement */}
          <section className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 mt-8">
            <p className="text-center text-slate-400">
              By using Option Wizard, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default TermsOfService;
